/**
 * Free-Audit lead-gen popup (used on all 3 landing pages).
 * Best-practice defaults applied throughout:
 *  - Triggers (first-fire wins, only once per visit): 45s dwell, 55% scroll, exit-intent
 *  - 7-day cooldown after dismiss; permanent hide after submit (localStorage)
 *  - ESC + backdrop close, focus trap, aria-modal, initial focus on the first field
 *  - GTM dataLayer events: audit_popup_view, audit_popup_submit, audit_popup_dismiss
 *  - Minimum-viable form fields per service (best-practice for popup conversion)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { trackEvent, getTrackingMeta } from "@/lib/tracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const TIME_TRIGGER_MS = 45 * 1000;
const SCROLL_TRIGGER_PCT = 55;

// -------- per-service config -----------------------------------------------

// service_type key must match the backend enum.
export const AUDIT_CONFIG = {
  paid_ads: {
    accent: "#1a73e8",
    eyebrow: "Wait — before you go",
    headline: "Get a free audit of your paid ads.",
    subhead:
      "Don't leave without it. I'll personally review your Google or Meta account and send you a Loom in 48 hours — 3 leaks to fix, no sales pitch.",
    cta: "Get my free audit",
    fields: [
      { name: "first_name", label: "First name", type: "text", required: true, placeholder: "Thato", autoComplete: "given-name" },
      { name: "business_email", label: "Business email", type: "email", required: true, placeholder: "you@company.co.za", autoComplete: "email" },
      {
        name: "ad_platform",
        label: "Which platform?",
        type: "select",
        required: true,
        options: [
          { value: "", label: "Choose one" },
          { value: "google", label: "Google Ads" },
          { value: "meta", label: "Meta (Facebook / Instagram)" },
          { value: "both", label: "Both" },
        ],
      },
      {
        name: "monthly_ad_spend",
        label: "Current monthly ad spend",
        type: "select",
        required: true,
        options: [
          { value: "", label: "Choose a range" },
          { value: "Under R5 000", label: "Under R5 000" },
          { value: "R5 000 – R15 000", label: "R5 000 – R15 000" },
          { value: "R15 000 – R50 000", label: "R15 000 – R50 000" },
          { value: "R50 000 – R150 000", label: "R50 000 – R150 000" },
          { value: "R150 000+", label: "R150 000+" },
        ],
      },
    ],
    proof: "48-hour turnaround · No obligation · Sent as a Loom",
  },
  whatsapp_automation: {
    accent: "#25d366",
    eyebrow: "Wait — before you go",
    headline: "Get a free audit of your WhatsApp.",
    subhead:
      "Don't leave without it. I'll review your current flow (or lack of one) and show you 3 leaks in your WhatsApp costing you sales — sent as a Loom in 48 hours.",
    cta: "Show me the leaks",
    fields: [
      { name: "first_name", label: "First name", type: "text", required: true, placeholder: "Thato", autoComplete: "given-name" },
      { name: "business_email", label: "Business email", type: "email", required: true, placeholder: "you@company.co.za", autoComplete: "email" },
      { name: "whatsapp_number", label: "WhatsApp business number", type: "tel", required: true, placeholder: "+27 XX XXX XXXX", autoComplete: "tel" },
      {
        name: "monthly_enquiries",
        label: "Enquiries you handle per month",
        type: "select",
        required: true,
        options: [
          { value: "", label: "Choose a range" },
          { value: "Under 50", label: "Under 50" },
          { value: "50 – 200", label: "50 – 200" },
          { value: "200 – 500", label: "200 – 500" },
          { value: "500 – 2000", label: "500 – 2 000" },
          { value: "2000+", label: "2 000+" },
        ],
      },
    ],
    proof: "48-hour turnaround · Reviewed by Thato personally",
  },
  website_app_creation: {
    accent: "#0B1120",
    eyebrow: "Wait — before you go",
    headline: "Are sales opportunities falling through the cracks?",
    subhead:
      "Get a free five-minute Lead-Loss Audit showing where enquiries, follow-up or sales processes may be failing.",
    cta: "Audit My Sales Process",
    fields: [
      { name: "website_url", label: "Website URL", type: "url", required: true, placeholder: "https://yourbusiness.co.za", autoComplete: "url" },
      { name: "business_email", label: "Business email", type: "email", required: true, placeholder: "you@company.co.za", autoComplete: "email" },
    ],
    proof: "No obligation · One practical video assessment",
  },
};

// Never interrupt a visitor who has started, or is looking at, a quote/plan form.
const FORM_IDS = ["request-plan", "request-quote", "quote-form"];
function userIsOnForm() {
  if (window.__rekisaFormStarted || window.__rekisaHeadingToForm) return true;
  if (FORM_IDS.includes(window.location.hash.slice(1))) return true;
  if (document.activeElement?.closest?.("form")) return true;
  return FORM_IDS.some((id) => {
    const r = document.getElementById(id)?.getBoundingClientRect();
    return r && r.top < window.innerHeight && r.bottom > 0;
  });
}

// -------- localStorage helpers --------------------------------------------

const DISMISS_KEY = (svc) => `rekisa_audit_dismissed_${svc}`;
const SUBMITTED_KEY = (svc) => `rekisa_audit_submitted_${svc}`;

function shouldSuppress(service) {
  try {
    if (localStorage.getItem(SUBMITTED_KEY(service))) return true;
    const raw = localStorage.getItem(DISMISS_KEY(service));
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isFinite(ts) && Date.now() - ts < DISMISS_TTL_MS) return true;
    localStorage.removeItem(DISMISS_KEY(service));
    return false;
  } catch {
    return false;
  }
}

// -------- component --------------------------------------------------------

export default function AuditPopup({ service }) {
  const config = AUDIT_CONFIG[service];
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState(null);
  const [values, setValues] = useState({});
  const firstFieldRef = useRef(null);
  const dialogRef = useRef(null);
  const hasFiredView = useRef(false);
  const hasTriggered = useRef(false);

  // ---- trigger logic (once per visit, whichever fires first) ----
  useEffect(() => {
    if (!config) return;
    // External trigger — bypasses the shouldSuppress guard because the user
    // actively clicked the sticky announcement (or another explicit CTA).
    const onExternalOpen = () => {
      hasTriggered.current = true;
      setOpen(true);
      trackEvent("audit_popup_view", { service, trigger: "external_event" });
    };
    window.addEventListener("rekisa:open-audit", onExternalOpen);

    if (shouldSuppress(service)) {
      return () => window.removeEventListener("rekisa:open-audit", onExternalOpen);
    }

    // Visitors clicking a form link/CTA are on their way to the form — don't intercept mid-scroll.
    const onFormLinkClick = (e) => {
      const a = e.target.closest?.("a[href*='#']");
      if (a && FORM_IDS.some((id) => a.getAttribute("href").endsWith(`#${id}`))) window.__rekisaHeadingToForm = true;
    };
    document.addEventListener("click", onFormLinkClick, true);

    const openOnce = (reason) => {
      if (hasTriggered.current || userIsOnForm()) return;
      hasTriggered.current = true;
      setOpen(true);
      trackEvent("audit_popup_view", { service, trigger: reason });
    };

    // 1) Time delay
    const timer = setTimeout(() => openOnce("time"), TIME_TRIGGER_MS);

    // 2) Scroll depth
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      const pct = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      if (pct >= SCROLL_TRIGGER_PCT) openOnce("scroll");
    };

    // 3) Exit intent (desktop): fire when the cursor actually leaves the
    // viewport — top, left, or right edges. We ignore the bottom edge because
    // browser chrome (dock, taskbar) can cause false positives there.
    // `mouseleave` on documentElement fires reliably when the pointer exits
    // the window in every modern browser.
    const onMouseLeave = (e) => {
      // clientY <= 0 → leaving via the top (address bar / tabs / close)
      // clientX <= 0 → leaving to the left edge
      // clientX >= innerWidth → leaving to the right edge
      const w = window.innerWidth;
      const leftOrRight = e.clientX <= 0 || e.clientX >= w - 1;
      const top = e.clientY <= 0;
      if (top || leftOrRight) openOnce("exit_intent");
    };

    // 4) Mobile exit-intent proxy: when the tab becomes hidden (user swipes
    // to another app, hits the home button, or switches tab). We only fire
    // this once and only if the user has spent >=8s on the page — otherwise
    // it fires on accidental app-switches.
    const pageOpenedAt = Date.now();
    const onVisibility = () => {
      if (document.visibilityState === "hidden" && Date.now() - pageOpenedAt > 8000) {
        openOnce("visibility_hidden");
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.documentElement.addEventListener("mouseleave", onMouseLeave);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onFormLinkClick, true);
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("rekisa:open-audit", onExternalOpen);
    };
  }, [service, config]);

  const dismiss = useCallback(
    (reason = "close") => {
      try {
        if (!submitted) localStorage.setItem(DISMISS_KEY(service), String(Date.now()));
      } catch {
        /* ignore quota errors */
      }
      trackEvent("audit_popup_dismiss", { service, reason, submitted });
      setOpen(false);
    },
    [service, submitted]
  );

  // ---- ESC to close + focus trap + lock body scroll ----
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        dismiss("escape");
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);

    // Initial focus
    setTimeout(() => firstFieldRef.current?.focus(), 30);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, dismiss]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    // Simple client-side required check
    for (const f of config.fields) {
      if (f.required && !(values[f.name] || "").trim()) {
        setFormError(`Please complete "${f.label}".`);
        return;
      }
    }
    setSubmitting(true);
    const email = (values.business_email || "").trim();
    const fallbackName = (values.first_name || "").trim() || (email.includes("@") ? email.split("@")[0] : "there");
    try {
      await axios.post(`${BACKEND_URL}/api/audit`, {
        service_type: service,
        first_name: fallbackName,
        business_email: email,
        ad_platform: values.ad_platform || null,
        monthly_ad_spend: values.monthly_ad_spend || null,
        whatsapp_number: values.whatsapp_number || null,
        monthly_enquiries: values.monthly_enquiries || null,
        website_url: values.website_url || null,
        tracking: getTrackingMeta(),
      });
      try {
        localStorage.setItem(SUBMITTED_KEY(service), "1");
      } catch {
        /* ignore quota errors */
      }
      trackEvent("audit_popup_submit", { service });
      setSubmitted(true);
      toast.success("Audit request received — check your inbox in 48 hours.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setFormError(typeof detail === "string" ? detail : "Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!config || !open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="audit-popup-title"
      data-testid={`audit-popup-${service}`}
    >
      <div
        className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={() => dismiss("backdrop")}
      />
      <div
        ref={dialogRef}
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Accent bar */}
        <div className="h-1.5" style={{ background: config.accent }} />

        <button
          onClick={() => dismiss("close_button")}
          className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 text-xl leading-none z-10"
          aria-label="Close"
          data-testid={`audit-popup-close-${service}`}
        >
          ×
        </button>

        {submitted ? (
          <div className="p-6 sm:p-8 text-center">
            <div
              className="mx-auto h-14 w-14 rounded-full flex items-center justify-center text-white text-2xl font-black mb-4"
              style={{ background: config.accent }}
            >
              ✓
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 mb-2">
              You&apos;re on the list.
            </h2>
            <p className="text-sm text-zinc-600 mb-5">
              I&apos;ll personally review what you&apos;re running and send your audit within <strong>48 hours</strong>.
              Watch your inbox — including Promotions or Spam just in case.
            </p>
            <button
              onClick={() => dismiss("submitted_close")}
              className="text-xs font-bold uppercase tracking-widest bg-zinc-900 text-white rounded-md px-4 py-2 hover:bg-black"
              data-testid={`audit-popup-thankyou-close-${service}`}
            >
              Back to the site
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 sm:p-8">
            <div
              className="text-[10px] font-black uppercase tracking-[0.2em] mb-2"
              style={{ color: config.accent }}
            >
              {config.eyebrow}
            </div>
            <h2 id="audit-popup-title" className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 leading-tight">
              {config.headline}
            </h2>
            <p className="mt-2 text-sm text-zinc-600">{config.subhead}</p>

            <div className="mt-5 space-y-3">
              {config.fields.map((f, idx) => {
                const common = {
                  id: `audit-${service}-${f.name}`,
                  name: f.name,
                  required: f.required,
                  onChange,
                  value: values[f.name] || "",
                  ref: idx === 0 ? firstFieldRef : undefined,
                  "data-testid": `audit-popup-field-${f.name}`,
                  className:
                    "w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-zinc-900",
                };
                return (
                  <div key={f.name}>
                    <label htmlFor={common.id} className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-1">
                      {f.label}
                    </label>
                    {f.type === "select" ? (
                      <select {...common}>
                        {f.options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input {...common} aria-label={f.label || f.placeholder} type={f.type} placeholder={f.placeholder} autoComplete={f.autoComplete} />
                    )}
                  </div>
                );
              })}
            </div>

            {formError && (
              <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                {formError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-5 w-full rounded-md py-3 font-black uppercase tracking-widest text-sm text-white transition-opacity disabled:opacity-60"
              style={{ background: config.accent }}
              data-testid={`audit-popup-submit-${service}`}
            >
              {submitting ? "Sending…" : config.cta}
            </button>

            <p className="mt-3 text-[11px] text-zinc-500 text-center">{config.proof}</p>
            <p className="mt-1 text-[10px] text-zinc-400 text-center">
              We&apos;ll only use your email to send the audit and follow up. No spam.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
